<script lang="ts">
export const elInputDateTypes = ['date', 'datetime-local', 'time', 'month'] as const;
export type ElInputDateType = (typeof elInputDateTypes)[number];
</script>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { useInput, NumberValueType, showRequiredMark, withNumberElInputProps } from '@/forms/input';
import { DateTime } from 'luxon';

const props = defineProps({
  ...withNumberElInputProps(),
  type: {
    type: String as PropType<ElInputDateType>,
    default: 'date',
  },
  zone: {
    type: String,
    default: undefined,
    required: false,
  },
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: NumberValueType): void;
  (event: 'update:readableString', value: string): void;
}>();

const initialDate = props.modelValue ? DateTime.fromSeconds(props.modelValue, { zone: props.zone }) : undefined;

const dateFormat = computed(() => {
  switch (props.type) {
    case 'date':
      return 'yyyy-MM-dd';
    case 'time':
      return 'HH:mm';
    case 'datetime-local':
      // eslint-disable-next-line quotes
      return `yyyy-MM-dd'T'HH:mm`;
    default:
      return '';
  }
});

const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);

const { value, errorMessage, uuid, fieldContext } = useInput(computedModelValue, computedValidation, props.name, newValue => {
  emit('update:modelValue', newValue);
  emit('update:readableString', valueToNumber.value);
});

// this is needed to have vee-validate only work on number values, this way we don't have to deal with date format, but only timestamps
const valueToNumber = computed<string>({
  get: () => {
    if (!value.value) return '';
    return DateTime.fromSeconds(value.value, { zone: props.zone }).toFormat(dateFormat.value);
  },
  set: (valueAsString: string) => {
    if (valueAsString) {
      let newDate = DateTime.fromFormat(valueAsString, dateFormat.value, { zone: props.zone });
      if (props.type === 'time' && initialDate) {
        // when parsing from time, it loses reference of the actual date. Set it manually
        newDate = newDate.set({ day: initialDate.day, month: initialDate.month, year: initialDate.year });
      }
      value.value = Math.round(newDate.toSeconds());
    } else {
      value.value = undefined;
    }
  },
});
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="uuid"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
  >
    <template #default="{ error }">
      <input
        :id="uuid"
        v-model="valueToNumber"
        :name="props.name"
        :type="type"
        class="el-input"
        :class="{
          error: error,
          'text-neutral-darker border-primary': valueToNumber !== '',
        }"
        :placeholder="placeholder"
        :disabled="props.disabled"
        :data-cy="`${label.trim().replaceAll(' ', '')}-input`"
        max="9999-12-31"
        @blur="fieldContext.validate()"
      />
    </template>
  </ElInputContainer>
</template>
